import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import PageMeta from '../../components/seo/PageMeta';
import { questionPageJsonLd } from '../../config/seo';
import {
  buildQuestionSeoDescription,
  buildQuestionSeoTitle,
  publicQuestionPath,
} from '../../utils/questionSeoText';
import { fetchPublicQuestion } from '../../utils/publicQuestionApi';
import FormattedQuestions from '../../components/Items/FormattedQuestions';
import FormattedAnswer from '../../components/Items/FormattedAnswer';
import '../../components/pages/styles/QuestionDetail.css';
import './public-question.css';

export default function PublicQuestionPage() {
  const { id } = useParams();
  const [item, setItem] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError('');
      setItem(null);
      try {
        const data = await fetchPublicQuestion(id);
        if (cancelled) return;
        if (!data) {
          setError('This question is not available for public viewing.');
        } else {
          setItem(data);
        }
      } catch (e) {
        if (!cancelled) setError(e.message || 'Could not load question.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [id]);

  const title = item ? buildQuestionSeoTitle(item) : 'Q&A';
  const description = item ? buildQuestionSeoDescription(item) : 'Structured Q&A on QnaRead.';
  const jsonLd = item ? questionPageJsonLd(item, id) : null;

  return (
    <div className="public-question-page">
      <PageMeta
        title={title}
        description={description}
        canonicalPath={publicQuestionPath(id)}
        ogType="article"
        jsonLd={jsonLd}
        noindex={false}
        nofollow={false}
      />
      <header className="public-question-header">
        <Link to="/product" className="public-question-brand">
          QnaRead
        </Link>
        <nav className="public-question-nav" aria-label="Public">
          <Link to="/features">Product tour</Link>
          <Link to="/for-developers">Interview prep</Link>
          <Link to="/login" className="public-question-nav-cta">
            Sign in to practice
          </Link>
        </nav>
      </header>

      <main className="public-question-main" id="main-content">
        {loading && <p className="public-question-muted" aria-live="polite">Loading…</p>}
        {error && !loading && (
          <p className="public-question-error" role="alert">{error}</p>
        )}
        {item && (
          <article className="question-detail-card public-question-card" itemScope itemType="https://schema.org/QAPage">
            <div className="learning-tags question-detail-tags">
              {item.topic && <span className="learning-tag">{item.topic}</span>}
              {item.subCategory2 && <span className="learning-tag learning-tag--muted">{item.subCategory2}</span>}
              {item.subCategory1 && <span className="learning-tag learning-tag--muted">{item.subCategory1}</span>}
              {item.notebookTitle && (
                <span className="learning-tag learning-tag--muted">{item.notebookTitle}</span>
              )}
            </div>
            <h1 className="question-detail-title" itemProp="name">
              <FormattedQuestions item={item} />
            </h1>
            <section className="question-detail-section" itemScope itemProp="mainEntity" itemType="https://schema.org/Question">
              <h2 className="public-question-answer-heading">Answer</h2>
              <div className="question-detail-answer" itemScope itemProp="acceptedAnswer" itemType="https://schema.org/Answer">
                <div itemProp="text">
                  <FormattedAnswer item={item} />
                </div>
              </div>
            </section>
            <p className="public-question-cta-copy">
              Save this card, run spaced repetition, and use AI explain when you{' '}
              <Link to={`/login?redirect=${encodeURIComponent(`/question/${id}`)}`}>sign in to QnaRead</Link>.
            </p>
            <div className="public-question-actions">
              <Link to={`/login?redirect=${encodeURIComponent(`/question/${id}`)}`} className="home-btn home-btn-primary">
                Open in app
              </Link>
              <Link to="/for-students" className="home-btn home-btn-secondary">
                Learn how QnaRead works
              </Link>
            </div>
          </article>
        )}
      </main>
      <footer className="public-question-footer">
        <Link to="/privacy">Privacy</Link>
        <Link to="/terms">Terms</Link>
        <Link to="/contact">Contact</Link>
      </footer>
    </div>
  );
}
